import type { CrmObjectTypeSchema } from '@/lib/crm/core/SchemaClient';
import type { NavObjectTypeItem } from '@/lib/crm/hooks/useObjectTypes';

import {
  clearAllPersistedCacheByPrefix,
  getPersistedCache,
  setPersistedCache,
} from './persistedCache';

const CACHE_VERSION = 2;
const STORAGE_KEY_PREFIX = 'ahoy-cache-object-types-';
const COMPONENT = 'persisted-object-types';

export interface PersistedObjectTypesData {
  items: NavObjectTypeItem[];
  allObjectTypes: CrmObjectTypeSchema[];
}

function isNavObjectTypeItemShape(item: unknown): boolean {
  if (typeof item !== 'object' || item === null) {
    return false;
  }
  const i = item as Record<string, unknown>;
  return (
    typeof i.entity_type_name === 'string' &&
    typeof i.label === 'string' &&
    typeof i.label_plural === 'string' &&
    typeof i.slug === 'string' &&
    (i.icon === null || i.icon === undefined || typeof i.icon === 'string') &&
    // `entity_kind` is required on NavObjectTypeItem (nav grouping reads it as
    // an array). Validate it here so persisted items written before the field
    // existed fail the shape check and are discarded, rather than hydrating an
    // item whose missing `entity_kind` would break consumers calling
    // `.includes()` on it.
    Array.isArray(i.entity_kind) &&
    i.entity_kind.every(k => typeof k === 'string')
  );
}

function isCrmObjectTypeSchemaShape(item: unknown): boolean {
  if (typeof item !== 'object' || item === null) {
    return false;
  }
  const i = item as Record<string, unknown>;
  return (
    typeof i.entity_type_name === 'string' &&
    typeof i.label === 'string' &&
    typeof i.label_plural === 'string' &&
    typeof i.description === 'string' &&
    typeof i.allows_custom_properties === 'boolean' &&
    Array.isArray(i.entity_kind) &&
    i.entity_kind.every(k => typeof k === 'string') &&
    (i.icon === undefined || typeof i.icon === 'string')
  );
}

function isPersistedObjectTypesShape(
  raw: unknown
): raw is PersistedObjectTypesData {
  if (typeof raw !== 'object' || raw === null) {
    return false;
  }
  const d = raw as Record<string, unknown>;
  return (
    Array.isArray(d.items) &&
    Array.isArray(d.allObjectTypes) &&
    d.items.every(isNavObjectTypeItemShape) &&
    d.allObjectTypes.every(isCrmObjectTypeSchemaShape)
  );
}

export function getPersistedObjectTypes(
  orgId: number | undefined
): PersistedObjectTypesData | undefined {
  if (orgId === undefined) {
    return undefined;
  }
  return getPersistedCache<PersistedObjectTypesData>(
    `${STORAGE_KEY_PREFIX}${orgId}`,
    CACHE_VERSION,
    isPersistedObjectTypesShape,
    COMPONENT
  );
}

export function setPersistedObjectTypes(
  orgId: number,
  data: PersistedObjectTypesData
): void {
  setPersistedCache(
    `${STORAGE_KEY_PREFIX}${orgId}`,
    data,
    CACHE_VERSION,
    COMPONENT
  );
}

export function clearAllPersistedObjectTypes(): void {
  clearAllPersistedCacheByPrefix(STORAGE_KEY_PREFIX, COMPONENT);
}
